import { jsonLdStringify } from '@sly/core/helpers/strings';
import Helmet from 'next/head';
import { array, bool, number, object, string } from 'prop-types';
import React from 'react';
import { host } from 'sly/config';
import {
  authorPageFunc,
  reviewerPageFunc,
} from 'sly/resourceCenter/components/Helmet/pageGenerators';

const stringifyReplacer = (k, v) => {
  if (k === 'hash' || k === 'key') {
    return undefined;
  }
  return v;
};

/**
 * @param {{
 *   path?: string,
 *   title: string,
 *   shortDescription: string,
 *   isArticlePage?: boolean,
 *   isAuthorPage?: boolean,
 *   isReviewerPage?: boolean,
 *   published_at?: string | null,
 *   updated_at?: string | null,
 *   author?: any,
 *   reviewer?: any,
 *   mainImage?: any,
 *   imgUrl?: string,
 *   worksConsulted?: any[],
 *   isHubPage?: boolean,
 *   [key: string]: any
 * }} props
 */
const HelmetComponent = ({
  path = '',
  title,
  shortDescription,
  isArticlePage = false,
  isAuthorPage = false,
  isReviewerPage,
  published_at: publishedAt,
  updated_at: updatedAt,
  author = {},
  reviewer,
  mainImage = {},
  imgUrl,
  worksConsulted,
  isHubPage = false,
}) => {
  const mainLd = {};
  mainLd['@context'] = 'https://schema.org';
  mainLd['@type'] = 'WebPage';
  mainLd.headline = `${title} | Seniorly`;
  mainLd.url = `${host}${path}`;

  let ldContent = {};

  if (isAuthorPage) {
    ldContent = authorPageFunc(author);
  }

  if (isReviewerPage) {
    ldContent = reviewerPageFunc(ldContent, reviewer);
  }

  if (isArticlePage) {
    ldContent['@context'] = 'https://schema.org';
    ldContent['@type'] = 'Article';
    ldContent.mainEntityOfPage = `${host}${path}`;
    ldContent.headline = title;
    ldContent.datePublished = publishedAt;
    ldContent.dateModified = updatedAt;
    ldContent.description = shortDescription;
    ldContent.author = {
      '@type': 'Person',
      name: author.fullName,
    };
    ldContent.publisher = {
      '@type': 'Organization',
      name: 'Seniorly',
      logo: {
        '@type': 'ImageObject',
        url: 'https://d1qiigpe5txw4q.cloudfront.net/assets/footer_logo.png',
        width: 84,
        height: 42,
      },
    };
    if (mainImage?.url) {
      ldContent.image = {
        '@type': 'ImageObject',
        url: mainImage.url,
        height: 500,
        width: 800,
      };
    }

    if (worksConsulted && worksConsulted?.length > 0) {
      ldContent.citation = worksConsulted.map((work) => {
        return {
          '@type': 'CreativeWork',
          name: work.name,
          sameAs: work.url,
          datePublished: work.datePublished,
          author: {
            '@type': work?.Type,
            name: work.author,
          },
        };
      });
    }

    if (reviewer) {
      const reviewerLd = {};
      reviewerPageFunc(reviewerLd, reviewer);
      mainLd.reviewedBy = reviewerLd;
    }
  }

  if (isHubPage) {
    ldContent['@context'] = 'https://schema.org';
    ldContent['@type'] = 'Article';
    ldContent.mainEntityOfPage = `${host}${path}`;
    ldContent.headline = title;
    ldContent.datePublished = publishedAt;
    ldContent.dateModified = updatedAt;
    ldContent.description = shortDescription;
    if (author) {
      ldContent.author = {
        '@type': 'Person',
        name: author.fullName,
      };
    }

    ldContent.publisher = {
      '@type': 'Organization',
      name: 'Seniorly',
      logo: {
        '@type': 'ImageObject',
        url: 'https://d1qiigpe5txw4q.cloudfront.net/assets/footer_logo.png',
        width: 84,
        height: 42,
      },
    };
    if (mainImage) {
      ldContent.image = {
        '@type': 'ImageObject',
        url: mainImage.url,
        height: 500,
        width: 800,
      };
    }
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
        {isArticlePage && <meta content={title} property='og:title' />}
        {imgUrl && <meta property='og:image' content={imgUrl} />}
        <meta name='description' content={shortDescription} />
        <meta content='Seniorly' name='author' />
        <meta content='English' name='language' />
        <meta content='summary' name='twitter:card' />
        <meta content='@seniorly' name='twitter:site' />
        <meta content={`${title} | Seniorly`} name='twitter:title' />
        <meta content={shortDescription} name='twitter:description' />
        <meta content='@seniorly' name='twitter:creator' />
        <meta content={`${title} | Seniorly`} property='og:title' />
        <meta content={shortDescription} property='og:description' />
        <meta content='website' property='og:type' />
        <meta content={`${host}${path}`} property='og:url' />
        <meta content={host} property='og:site_url' />
        <meta content='Seniorly Inc.' property='og:site_name' />
        <link rel='canonical' href={`${host}${path}`} />
        <meta content='IE=edge' httpEquiv='X-UA-Compatible' />
        <meta content='width=device-width, initial-scale=1' name='viewport' />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: jsonLdStringify(mainLd, stringifyReplacer),
          }}
        />
        {(isArticlePage || isAuthorPage || isReviewerPage || isHubPage) && (
          <script
            type='application/ld+json'
            dangerouslySetInnerHTML={{
              __html: jsonLdStringify(ldContent, stringifyReplacer),
            }}
          />
        )}
      </Helmet>
    </>
  );
};

HelmetComponent.propTypes = {
  title: string.isRequired,
  shortDescription: string.isRequired,
  path: string,
  isArticlePage: bool,
  isAuthorPage: bool,
  published_at: string,
  updated_at: string,
  author: object,
  mainImage: object,
  imgUrl: string,
  pageNumber: number,
  articlesCount: number,
  worksConsulted: array,
  reviewer: object,
  isReviewerPage: bool,
  isHubPage: bool,
  content: object,
};

export default HelmetComponent;
